import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  useExpirePluginApiKey,
  usePluginApiKeys,
} from '@/hooks/api/useApiKeys';
import { pluginKeyStatus } from '@/lib/plugin-utils';
import { formatDateTime } from '@/lib/date-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';

interface PluginApiKeyListProps {
  organizationId: string;
}

export function PluginApiKeyList({ organizationId }: PluginApiKeyListProps) {
  const timeZone = useDisplayTimeZone();
  const { data, isLoading, error } = usePluginApiKeys(organizationId);
  const expireMutation = useExpirePluginApiKey(organizationId);
  const { toast } = useToast();

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">Loading plugin keys…</div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-destructive">
        Failed to load plugin keys.
      </div>
    );
  }

  const keys = data ?? [];
  if (keys.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No plugin API keys yet. Visit Get Started → Claude Desktop and click{' '}
        <strong>Download Plugin</strong> to mint your first one.
      </div>
    );
  }

  const handleExpire = (keyId: string): void => {
    expireMutation.mutate(keyId, {
      onSuccess: () => {
        toast({
          title: 'Plugin key expired',
          description: 'The key will no longer authenticate plugin requests.',
        });
      },
      onError: () => {
        toast({
          title: 'Could not expire plugin key',
          description: 'Please try again.',
          variant: 'destructive',
        });
      },
    });
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Prefix</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Expires</TableHead>
          <TableHead>Last used</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {keys.map(key => {
          const status = pluginKeyStatus(key);
          const isActiveAndUnexpired = status === 'Active';
          return (
            <TableRow key={key.id}>
              <TableCell className="font-mono text-xs">
                {key.keyPrefix}…
              </TableCell>
              <TableCell>{formatDateTime(key.createdAt, timeZone)}</TableCell>
              <TableCell>
                {key.expiresAt
                  ? formatDateTime(key.expiresAt, timeZone)
                  : 'Never'}
              </TableCell>
              <TableCell>{formatDateTime(key.lastUsedAt, timeZone)}</TableCell>
              <TableCell>
                <Badge
                  variant={
                    status === 'Active'
                      ? 'secondary'
                      : status === 'Expired'
                        ? 'outline'
                        : 'destructive'
                  }
                >
                  {status}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                {isActiveAndUnexpired && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleExpire(key.id)}
                    disabled={expireMutation.isPending}
                  >
                    Expire
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
